import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';

import { SkippedFilesExplainerModal } from './skipped-files-explainer-modal';
import { UploadFailureReason } from '../../../../../../../../../../backend/src/types/types';
import { FAILURE_REASON_CONFIG } from '../../utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useRetryFailedRelativityUpload } from '@/api/mutations/useRetryFailedRelativityUpload';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetUpload } from '@/api/queries/useGetUpload';
import { UploadFailure } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { downloadFailedFilesReport } from '@/helpers/report-utils';
import { AttemptedSummary } from '@/new/components/attempted-summary';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { includedInArr, selectColumn } from '@/new/lib/table-utils';

const columnHelper = createColumnHelper<UploadFailure>();
const EMPTY_UPLOAD_FAILURES: UploadFailure[] = [];

interface UploadDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  uploadId: string;
  unpackingSkippedCount?: number;
  duplicateIncludedCount?: number;
}

export function UploadDetailModal({
  open,
  onOpenChange,
  matterId,
  uploadId,
  unpackingSkippedCount = 0,
  duplicateIncludedCount,
}: UploadDetailModalProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [explainerReason, setExplainerReason] = useState<UploadFailureReason | null>(null);

  const openExplainer = useCallback((reason: UploadFailureReason) => {
    setExplainerReason(reason);
  }, []);
  const { data: upload, isLoading } = useGetUpload(matterId, uploadId);
  const { data: matter } = useGetMatterV2(matterId);
  const { mutate: retryFailedRelativityUpload, isLoading: isRetrying } = useRetryFailedRelativityUpload();

  const successCount = upload?.successfullyUploadedFileCount ?? 0;
  const failureCount = upload?.uploadFailures?.length ?? 0;

  const dedupeDisabled = upload?.deduplicateDocuments === false;
  const includedDuplicateCount = duplicateIncludedCount ?? (dedupeDisabled ? (upload?.duplicateCount ?? 0) : 0);

  const hasRelativityEnabled = !!matter?.relativityWorkspaceId;
  const isRelativityUpload = !!upload?.relativityImport;

  const uploadFailures = useMemo(() => upload?.uploadFailures ?? EMPTY_UPLOAD_FAILURES, [upload?.uploadFailures]);
  const reasonOptions = useMemo(
    () => Array.from(new Set(uploadFailures.map((failure) => failure.rejectedReason))).sort(),
    [uploadFailures],
  );

  const selectedReasonFilter = tableReasonFilterValue(columnFilters);

  useEffect(() => {
    setRowSelection({});
    setColumnFilters([]);
  }, [uploadId]);

  const columns = useMemo(
    () => [
      ...(isRelativityUpload ? [selectColumn<UploadFailure>()] : []),
      columnHelper.accessor('filename', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-800">{info.getValue()}</span>
          </div>
        ),
      }),
      ...(hasRelativityEnabled
        ? [
            columnHelper.accessor('controlNumber', {
              header: () => <span>Control Number</span>,
              cell: (info) => {
                const value = info.getValue();
                return value ? (
                  <span className="text-sm text-gray-700">{value}</span>
                ) : (
                  <span className="text-sm text-gray-400">&mdash;</span>
                );
              },
            }),
          ]
        : []),
      columnHelper.accessor('rejectedReason', {
        header: () => <span>Reason</span>,
        filterFn: includedInArr,
        cell: (info) => {
          const reason = info.getValue();
          const config = reason ? FAILURE_REASON_CONFIG[reason as keyof typeof FAILURE_REASON_CONFIG] : null;
          if (!config || !reason) {
            return <span className="text-sm text-gray-500">{reason}</span>;
          }
          return (
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-gray-700">{config.label}</span>

              <button
                type="button"
                className="text-gray-400 hover:text-teal-500"
                onClick={() => openExplainer(reason as UploadFailureReason)}
              >
                <Icon className="lucide--info h-3.5 w-3.5" />
              </button>
            </div>
          );
        },
      }),
      columnHelper.accessor('duplicateFile', {
        header: () => <span>Duplicate Of</span>,
        cell: (info) => {
          const reason = info.row.original.rejectedReason;
          if (reason !== UploadFailureReason.Duplicate) {
            return <span className="text-sm text-gray-400">&mdash;</span>;
          }

          const duplicateFile = info.getValue();
          if (!duplicateFile) return <span className="text-sm text-gray-400">&mdash;</span>;

          return (
            <div className="flex items-center gap-2">
              {duplicateFile.doc?.id ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="h-auto px-0 py-0 font-medium text-blue-500"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (!matter?.allDocumentsCaseId) return;
                        window.open(
                          `/matters/${matterId}/subsets/${matter.allDocumentsCaseId}/docs/${duplicateFile.doc?.id}`,
                          '_blank',
                        );
                      }}
                    >
                      {duplicateFile.name.length > 12 ? `${duplicateFile.name.slice(0, 12)}...` : duplicateFile.name}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{duplicateFile.name}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm text-gray-600">{duplicateFile.name}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{duplicateFile.name}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Date</span>,
        cell: (info) => {
          const date = info.getValue();
          return <span className="text-sm text-gray-600">{date ? formatDate(date, true, true) : '—'}</span>;
        },
      }),
    ],
    [matter?.allDocumentsCaseId, hasRelativityEnabled, isRelativityUpload, openExplainer],
  );

  const table = useReactTable({
    data: uploadFailures,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: false,
    state: {
      globalFilter,
      rowSelection,
      columnFilters,
    },
    getRowId: (row) => row.id,
    enableRowSelection: isRelativityUpload,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  const selectedFailures = table.getSelectedRowModel().rows.map((row) => row.original);

  const clearAllFilters = () => {
    setGlobalFilter('');
    setColumnFilters([]);
  };

  const confirmRetrySelectedDocuments = () => {
    retryFailedRelativityUpload(
      {
        matterId,
        uploadId,
        uploadFailureIds: selectedFailures.map((failure) => failure.id),
      },
      {
        onSuccess: () => {
          setRowSelection({});
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-6xl flex-col">
        <DialogHeader>
          <DialogTitle>Upload Details</DialogTitle>
          <DialogDescription>
            {upload && (
              <span className="flex items-center gap-2">
                <span>{formatDate(upload.createdAt, true, true)}</span>
                {upload.createdBy?.email && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span>{upload.createdBy.email}</span>
                  </>
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3">
          <AttemptedSummary
            attempted={successCount + failureCount}
            successful={successCount}
            failed={failureCount}
            successLabel="successfully uploaded"
            failedLabel="skipped"
          />
        </div>

        {/* TODO: consolidate these messages by flagging the upload settings for every upload in this modal */}
        {unpackingSkippedCount > 0 && (
          <div className="mb-2.5 flex items-start gap-2 rounded-md border border-gray-100 bg-gray-50 px-2.5 py-2 text-xs text-gray-900">
            <Icon className="lucide--mail mt-0.5 h-3 w-3 text-gray-500" />
            <span>Email attachments were not extracted because email unpacking was disabled for this upload.</span>
          </div>
        )}

        {includedDuplicateCount > 0 && (
          <div className="mb-2.5 flex items-start gap-2 rounded-md border border-gray-100 bg-gray-50 px-2.5 py-2 text-xs text-gray-900">
            <Icon className="lucide--copy mt-0.5 h-3 w-3 text-gray-500" />
            <span>
              {includedDuplicateCount.toLocaleString()} duplicate
              {includedDuplicateCount !== 1 ? 's were' : ' was'} included because deduplication was disabled for this
              upload.
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1">
          {failureCount > 0 ? (
            <ScrollableTable
              table={table}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              plural="skipped files"
              loading={isLoading}
              className="h-full max-h-[50vh] px-1 pt-1"
              filters={{
                popover: (
                  <FilterPopoverContent clearAllFilters={clearAllFilters}>
                    <div>
                      <p className="mb-3 text-gray-600">Reason</p>
                      <FilterMultiSelect
                        values={selectedReasonFilter}
                        options={reasonOptions}
                        column={table.getColumn('rejectedReason')!}
                        placeholder="Select reasons..."
                        formatLabel={(reason) => FAILURE_REASON_CONFIG[reason as UploadFailureReason]?.label ?? reason}
                      />
                    </div>
                  </FilterPopoverContent>
                ),
                count: columnFilters.length,
                clearAll: clearAllFilters,
              }}
              additionalButtons={
                <div className="ml-auto flex items-center gap-2">
                  {isRelativityUpload && (
                    <Button
                      variant="default"
                      onClick={confirmRetrySelectedDocuments}
                      disabled={selectedFailures.length === 0 || isRetrying}
                    >
                      {isRetrying ? (
                        <Icon className="lucide--loader-2 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Icon className="lucide--rotate-ccw h-3.5 w-3.5" />
                      )}
                      Retry Selected Documents ({selectedFailures.length})
                    </Button>
                  )}
                  <Button
                    variant="default"
                    onClick={() => {
                      trackCustomEvent(TrackingEventsV2.FileHistoryDownloadUploadBatchSkippedReport, { matterId });
                      upload?.uploadFailures &&
                        downloadFailedFilesReport(upload.uploadFailures, uploadId, {
                          includeControlNumber: hasRelativityEnabled,
                        });
                    }}
                    disabled={failureCount === 0}
                  >
                    <Icon className="lucide--download h-3.5 w-3.5" />
                    Download Skipped Files Report
                  </Button>
                </div>
              }
            />
          ) : (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              {isLoading ? (
                <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
              ) : (
                <div className="flex items-center gap-2">
                  <Icon className="lucide--check-circle size-3.5 text-green-500" />
                  <span className="text-sm text-gray-800">All files in this batch were uploaded successfully.</span>
                </div>
              )}
            </div>
          )}
        </div>

        <SkippedFilesExplainerModal
          open={explainerReason !== null}
          onOpenChange={(open) => !open && setExplainerReason(null)}
          highlightReason={explainerReason}
        />
      </DialogContent>
    </Dialog>
  );
}

function tableReasonFilterValue(columnFilters: ColumnFiltersState): string[] {
  const filterValue = columnFilters.find((filter) => filter.id === 'rejectedReason')?.value;
  return Array.isArray(filterValue) ? filterValue : [];
}
